import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage, ChatAttachment } from '../types/chat';
import { ChatService } from '../services/chatService';

interface ChatProps {
  className?: string;
}

export const Chat: React.FC<ChatProps> = ({ className = '' }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedModel, setSelectedModel] = useState('sonoma-dusk-alpha');
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Загружаем доступные модели при монтировании компонента
  useEffect(() => {
    const loadModels = async () => {
      try {
        const models = await ChatService.getAvailableModels();
        setAvailableModels(models);
      } catch (error) {
        console.error('Ошибка при загрузке моделей:', error);
      }
    };
    loadModels();
  }, []);

  // Автоскролл к последнему сообщению
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() && attachedFiles.length === 0) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: inputMessage,
      role: 'user',
      model: selectedModel,
      attachments: attachedFiles.map(file => ({
        filename: file.name,
        contentType: file.type,
        content: '' // Будет заполнено при отправке
      })),
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setAttachedFiles([]);
    setIsLoading(true);

    try {
      let response;
      if (attachedFiles.length > 0) {
        response = await ChatService.sendMessageWithFiles(
          inputMessage,
          selectedModel,
          attachedFiles
        );
      } else {
        response = await ChatService.sendMessage({
          message: inputMessage,
          model: selectedModel,
          attachments: []
        });
      }

      const assistantMessage: ChatMessage = {
        id: response.id,
        content: response.content,
        role: 'assistant',
        model: response.model,
        attachments: response.attachments,
        timestamp: response.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Ошибка при отправке сообщения:', error);
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        content: 'Извините, произошла ошибка при отправке сообщения.',
        role: 'assistant',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAttachedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      {/* Заголовок чата */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">AI Чат</h2>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Модель:</label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableModels.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Область сообщений */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <p>Начните диалог с AI моделью</p>
            <p className="text-sm mt-2">Вы можете прикреплять файлы для анализа</p>
            <div className="text-xs mt-4 text-gray-400">
              <p>Поддерживаемые форматы:</p>
              <p>📄 PDF • 📝 TXT • 📋 MD, JSON, XML • 📊 CSV</p>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                message.role === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <div className="whitespace-pre-wrap">{message.content}</div>
              
              {/* Прикрепленные файлы */}
              {message.attachments && message.attachments.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-300">
                  <div className="text-xs opacity-75">Прикрепленные файлы:</div>
                  {message.attachments.map((attachment, index) => (
                    <div key={index} className="text-xs mt-1 flex items-center">
                      {attachment.filename.toLowerCase().endsWith('.pdf') ? '📄' : '📎'} {attachment.filename}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="text-xs opacity-75 mt-1">
                {formatTimestamp(message.timestamp)}
                {message.model && ` • ${message.model}`}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span>AI думает...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Прикрепленные файлы */}
      {attachedFiles.length > 0 && (
        <div className="border-t border-gray-200 p-3">
          <div className="text-sm text-gray-600 mb-2">Прикрепленные файлы:</div>
          <div className="flex flex-wrap gap-2">
            {attachedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full text-sm"
              >
                <span>
                  {file.name.toLowerCase().endsWith('.pdf') ? '📄' : 
                   file.name.toLowerCase().endsWith('.txt') ? '📝' :
                   file.name.toLowerCase().endsWith('.md') ? '📋' :
                   file.name.toLowerCase().endsWith('.json') ? '📋' :
                   file.name.toLowerCase().endsWith('.xml') ? '📋' :
                   file.name.toLowerCase().endsWith('.csv') ? '📊' : '📎'} {file.name}
                </span>
                <button
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Поле ввода */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex space-x-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-3 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
            title="Прикрепить файлы"
          >
            📎
          </button>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Введите сообщение..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={isLoading || (!inputMessage.trim() && attachedFiles.length === 0)}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Отправить
          </button>
        </div>
      </div>
    </div>
  );
};
